import { useState, useEffect, useRef } from 'react';
import { DateInput } from '../../Input';
import { PrimaryButton, SecondaryButton } from '../../buttons';
import { toast } from 'react-hot-toast';
import { Delete01Icon, PlusSignIcon, Copy01Icon } from "hugeicons-react";
import api, { API_URL } from '../../../../api';
import '../../../../assets/css/components/forms/bulk-measure-data.css';
import CancelConfirmation from '../../../common/CancelConfirmation';

const BulkAddMeasureData = ({ onClose, onAddSuccess }) => {
    const [startingDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateError, setDateError] = useState('');
    const [isDateValid, setIsDateValid] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorDetails, setErrorDetails] = useState([]);
    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const [hasYearData, setHasYearData] = useState(true);
    const [measures, setMeasures] = useState([]);
    const [hospitals, setHospitals] = useState([]);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const [measureDataEntries, setMeasureDataEntries] = useState([
        { id: 1, measure: '', hospital: '', value: '', measureUnit: 'numerical' }
    ]);

    const [availableMeasures, setAvailableMeasures] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const popupRef = useRef(null);

    const formatDate = (dateObj) => {
        if (!dateObj) return null;

        try {
            if (dateObj.$y && dateObj.$M !== undefined && dateObj.$D) {
                const year = dateObj.$y;
                const month = (dateObj.$M + 1).toString().padStart(2, '0');
                const day = dateObj.$D.toString().padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            const date = new Date(dateObj);
            if (isNaN(date.getTime())) {
                console.error('Invalid date:', dateObj);
                return null;
            }

            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return null;
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isDatePickerOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, isDatePickerOpen]);

    const fetchMeasures = async (year) => {
        try {
            console.log('Fetching measures for year (new endpoint):', year);
            const response = await api.get(`${API_URL}/measures/by-year/`, {
                params: { year }
            });
            console.log('Measures API Response:', {
                status: response.status,
                data: response.data
            });

            let measuresData = response.data?.measures || [];
            if (!Array.isArray(measuresData)) {
                measuresData = [];
            }

            const measuresArray = measuresData.map(item => item.name);
            console.log('Processed measures:', measuresArray);

            setMeasures(measuresArray);
            setHasYearData(measuresArray.length > 0);

            if (measuresArray.length === 0) {
                toast.error(`No measures found for year ${year}. Please check the year or add measures.`, { duration: 300000, style: { minWidth: '300px' }, icon: '⚠️' });
            }
        } catch (error) {
            console.error('Error fetching measures:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            toast.error('Failed to load measures. Please try again.', { duration: 300000, style: { minWidth: '300px' }, icon: '⚠️' });
            setMeasures([]);
            setHasYearData(false);
        }
    };

    const fetchMeasureUnit = async (measureName, year) => {
        try {
            if (!measureName || !year) return 'numerical';

            console.log('Fetching measure unit for:', { measureName, year });
            const response = await api.get(`${API_URL}/measures/measure_unit/`, {
                params: {
                    measure_name: measureName,
                    measure_year: year
                }
            });
            console.log('Measure Unit Response:', response.data);

            return response.data?.type || 'numerical';
        } catch (error) {
            console.error('Error fetching measure unit:', error);
            return 'numerical';
        }
    };

    useEffect(() => {
        const fetchHospitals = async () => {
            try {
                console.log('Fetching hospitals...');
                const response = await api.get(`${API_URL}/hospitals/`);
                console.log('Hospitals API Response:', response.data);

                let hospitalsData = [];
                if (response.data && response.data.results && Array.isArray(response.data.results)) {
                    hospitalsData = response.data.results;
                } else if (Array.isArray(response.data)) {
                    hospitalsData = response.data;
                }

                const hospitalArray = hospitalsData.map(item => item.name);
                console.log('Processed hospitals:', hospitalArray);
                setHospitals(hospitalArray);

                if (hospitalArray.length === 0) {
                    toast.error('No hospitals found.', { duration: 300000, style: { minWidth: '300px' }, icon: '⚠️' });
                }
            } catch (error) {
                console.error('Error fetching hospitals:', error);
                toast.error('Failed to load hospitals. Please try again.', { duration: 300000, style: { minWidth: '300px' }, icon: '⚠️' });
            }
        };
        fetchHospitals();
    }, []);

    useEffect(() => {
        if (!startingDate || !endDate) {
            setIsDateValid(false);
            return;
        }

        try {
            if (startingDate.$y === undefined || endDate.$y === undefined) {
                setIsDateValid(false);
                return;
            }

            if (startingDate.$y !== endDate.$y) {
                setIsDateValid(false);
                setDateError("Both Starting Date and Ending date should be in the same year.");
                return;
            }

            if (startingDate.$m > endDate.$m || (startingDate.$m === endDate.$m && startingDate.$d > endDate.$d)) {
                setIsDateValid(false);
                setDateError("Ending date should be greater than Starting Date.");
                return;
            }

            const year = startingDate.$y;
            fetchMeasures(year);
            setIsDateValid(true);
            setDateError('');
        } catch (error) {
            console.error('Error validating dates:', error.message);
            setIsDateValid(false);
        }
    }, [startingDate, endDate]);

    const fetchExistingMeasureData = async (hospitalName, year, startDate, endDate) => {
        try {
            console.log('Fetching existing measure data for:', { hospitalName, year, startDate, endDate });
            const response = await api.get(`${API_URL}/hospitals/hospital/${hospitalName}/?year=${year}`);

            console.log('API Response data:', response.data);

            if (response.data && response.data.measure_table_data && response.data.measure_table_data[year]) {
                const measureData = response.data.measure_table_data[year];
                const existingMeasures = new Set();
              
                Object.keys(measureData).forEach(measureName => {
                    const months = measureData[measureName].months;
                    console.log(`Checking measure ${measureName}, months:`, months);

                    if (months) {
                        const startMonth = startDate.$m + 1;
                        const endMonth = endDate.$m + 1;

                        console.log(`Checking months ${startMonth} to ${endMonth} for measure ${measureName}`);

                        for (let month = startMonth; month <= endMonth; month++) {
                            const value = months[month];
                            console.log(`Month ${month} value:`, value);
                            if (value != null && value !== 0 && value !== '') {
                                console.log(`Found existing data for measure ${measureName} in month ${month}`);
                                existingMeasures.add(measureName);
                                break;
                            }
                        }
                    }
                });

                console.log('Existing measures with data:', Array.from(existingMeasures));
                return existingMeasures;
            }
            console.log('No measure_table_data found in response');
            return new Set();
        } catch (error) {
            console.error('Error fetching existing measure data:', error);
            console.error('Error response:', error.response?.data);
            return new Set();
        }
    };

    useEffect(() => {
        const filterAvailableMeasures = async () => {
            if (!selectedHospital || !startingDate || !endDate || !isDateValid) {
                setAvailableMeasures(measures);
                return;
            }

            const year = startingDate.$y;
            const existingMeasures = await fetchExistingMeasureData(selectedHospital, year, startingDate, endDate);

            console.log('All measures:', measures);
            console.log('Existing measures to filter out:', Array.from(existingMeasures));

            const filteredMeasures = measures.filter(measure => !existingMeasures.has(measure));
            setAvailableMeasures(filteredMeasures);

            console.log('Filtered available measures:', filteredMeasures);
        };

        filterAvailableMeasures();
    }, [selectedHospital, startingDate, endDate, isDateValid, measures]);

    const addNewEntry = () => {
        const newId = Math.max(...measureDataEntries.map(entry => entry.id)) + 1;
        setMeasureDataEntries([
            ...measureDataEntries,
            { id: newId, measure: '', hospital: selectedHospital, value: '', measureUnit: 'numerical' }
        ]);
    };

    const resetForm = () => {
        setSelectedHospital('');
        setMeasureDataEntries([
            { id: 1, measure: '', hospital: '', value: '', measureUnit: 'numerical' }
        ]);
        setAvailableMeasures([]);
    };

    const removeEntry = (id) => {
        if (measureDataEntries.length > 1) {
            setMeasureDataEntries(measureDataEntries.filter(entry => entry.id !== id));
        }
    };

    const duplicateEntry = (id) => {
        const entryToDuplicate = measureDataEntries.find(entry => entry.id === id);
        if (entryToDuplicate) {
            const newId = Math.max(...measureDataEntries.map(entry => entry.id)) + 1;
            const duplicatedEntry = {
                ...entryToDuplicate,
                id: newId,
                value: '',
                hospital: selectedHospital
            };
            setMeasureDataEntries([...measureDataEntries, duplicatedEntry]);
        }
    };

    const updateEntry = async (id, field, value) => {
        const updatedEntries = measureDataEntries.map(entry => {
            if (entry.id === id) {
                const updatedEntry = { ...entry, [field]: value, hospital: selectedHospital };

                if (field === 'measure' && value && startingDate?.$y) {
                    fetchMeasureUnit(value, startingDate.$y).then(unit => {
                        setMeasureDataEntries(prev =>
                            prev.map(e =>
                                e.id === id ? { ...e, measureUnit: unit } : e
                            )
                        );
                    });
                }

                return updatedEntry;
            }
            return entry;
        });
        setMeasureDataEntries(updatedEntries);
    };

    const handleHospitalChange = (value) => {
        setSelectedHospital(value);
        setMeasureDataEntries(prev =>
            prev.map(entry => ({ ...entry, hospital: value }))
        );
    };

    const validateEntries = () => {
        const errors = [];
        measureDataEntries.forEach((entry, index) => {
            if (!entry.measure) {
                errors.push(`Row ${index + 1}: Please select a measure`);
            }
            if (!selectedHospital) {
                errors.push(`Please select a hospital first`);
            }

            if (entry.value === '' || entry.value === null || entry.value === undefined) {
                errors.push(`Row ${index + 1}: Please enter a value (0 is allowed)`);
            }
        });
        return errors;
    };

    const handleBulkSubmit = async () => {
        const validationErrors = validateEntries();
        if (validationErrors.length > 0) {
            toast.error(validationErrors[0], {
                duration: 200000,
                style: { minWidth: '300px' },
                icon: '⚠️',
            });
            return;
        }

        const startingDateFormatted = formatDate(startingDate);
        const endingDateFormatted = formatDate(endDate);

        if (!startingDateFormatted || !endingDateFormatted) {
            toast.error('Invalid date format', { duration: 200000, style: { minWidth: '300px' }, icon: '⚠️' });
            return;
        }

        setIsSaving(true);
        setErrorDetails([]);
        setShowErrorSummary(false);

        try {
            const measureDataArray = measureDataEntries.map(entry => ({
                measure: entry.measure,
                hospital: entry.hospital,
                value: entry.value,
                starting_date: startingDateFormatted,
                end_date: endingDateFormatted
            }));


            const batchSize = 5;
            const batches = [];
            for (let i = 0; i < measureDataArray.length; i += batchSize) {
                batches.push(measureDataArray.slice(i, i + batchSize));
            }

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            const progressToast = toast.loading(`Processing ${measureDataArray.length} entries...`);

            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                const batch = batches[batchIndex];

                toast.loading(`Processing batch ${batchIndex + 1} of ${batches.length}...`, {
                    id: progressToast
                });

                const batchPromises = batch.map(async (data, index) => {
                    try {
                        const response = await api.post(`${API_URL}/measures/new_measure_data/`, data);
                        return { success: true, data, response };
                    } catch (error) {
                        const errorMsg = error?.response?.data?.message ||
                            error?.response?.data?.error ||
                            error?.message ||
                            'Unknown error';

                        const conflictName = error?.response?.data?.conflict_measure || data.measure;

                        return {
                            success: false,
                            data,
                            error: {
                                status: error?.response?.status,
                                message: errorMsg,
                                conflictName,
                                originalIndex: batchIndex * batchSize + index + 1
                            }
                        };
                    }
                });

                const batchResults = await Promise.all(batchPromises);

                batchResults.forEach(result => {
                    if (result.success) {
                        successCount++;
                    } else {
                        errorCount++;
                        errors.push(result.error);
                    }
                });

                if (batchIndex < batches.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            toast.dismiss(progressToast);

            if (successCount > 0 && errorCount === 0) {
                toast.success(`Successfully added all ${successCount} measure data entries!`);

                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (typeof onAddSuccess === 'function') {
                        onAddSuccess();
                    }
                }, 1500);

                setTimeout(() => {
                    window.location.reload();
                }, 3000);

            } else if (successCount > 0 && errorCount > 0) {
                toast.success(`Successfully added ${successCount} entries`);

                setTimeout(() => {
                    setErrorDetails(errors);
                    setShowErrorSummary(true);
                    toast.error(`${errorCount} entries failed to save. Check the error summary below.`, {
                        duration: 100000,
                        style: { minWidth: '300px' },
                        icon: '⚠️'
                    });
                }, 2000);

                setTimeout(() => {
                    resetForm();
                    if (typeof onAddSuccess === 'function') {
                        onAddSuccess();
                    }
                }, 4000);

            } else {
                setTimeout(() => {
                    setErrorDetails(errors);
                    setShowErrorSummary(true);
                    toast.error('Failed to add any measure data entries. Please check the errors below.', {
                        duration: 100000,
                        style: { minWidth: '300px' },
                        icon: '⚠️'
                    });
                }, 1000);
            }

        } catch (error) {
            console.error('Unexpected error during bulk submission:', error);
            setTimeout(() => {
                toast.error('An unexpected error occurred. Please try again.', {
                    duration: 300000,
                    style: { minWidth: '300px' },
                    icon: '⚠️'
                });
            }, 2000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelClick = () => {
        const hasUnsavedData = measureDataEntries.some(entry =>
            entry.measure || entry.value || (entry.hospital && entry.hospital !== selectedHospital)
        );

        if (hasUnsavedData) {
            setShowCancelConfirm(true);
        } else {
            onClose();
        }
    };

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        onClose();
    };

    const cancelCancel = () => {
        setShowCancelConfirm(false);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isDatePickerOpen) {
            handleCancelClick();
        }
    };

    return (
        <div className="bulk-measure-overlay" onClick={handleOverlayClick}>
            <div className="bulk-measure-form" ref={popupRef}>
                <h2>Add Multiple Measure Data</h2>

                <div className="date-range-section">

                    <div className="date-inputs">
                        <div className="date-field">
                            <label>Start Date</label>
                            <div
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={() => setIsDatePickerOpen(true)}
                                onBlur={() => setTimeout(() => setIsDatePickerOpen(false), 200)}
                            >
                                <DateInput
                                    date={startingDate}
                                    setDate={setStartDate}
                                    placeholder="Starting date"
                                />
                            </div>
                        </div>
                        <div className="date-field">
                            <label>End Date</label>
                            <div
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={() => setIsDatePickerOpen(true)}
                                onBlur={() => setTimeout(() => setIsDatePickerOpen(false), 200)}
                            >
                                <DateInput
                                    date={endDate}
                                    setDate={setEndDate}
                                    placeholder="End date"
                                />
                            </div>
                        </div>
                    </div>

                    {dateError && (
                        <p className="date-error">{dateError}</p>
                    )}
                </div>

                {startingDate && endDate && isDateValid && hasYearData ? (
                    <div className="bulk-entries-section">
                        <div className="hospital-selection-section">
                            <label>Hospital</label>
                            <select
                                value={selectedHospital}
                                onChange={(e) => handleHospitalChange(e.target.value)}
                                className="hospital-select"
                                required
                            >
                                <option value="">Select Hospital</option>
                                {hospitals.map((hospital, idx) => (
                                    <option key={idx} value={hospital}>
                                        {hospital}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedHospital && (
                            <>
                                <div className="entries-header">
                                    <PrimaryButton
                                        iconClass={<PlusSignIcon />}
                                        buttonText="Add Row"
                                        onClick={addNewEntry}
                                    />
                                </div>

                                <div className="entries-table">
                                    <div className="table-header">
                                        <div className="header-cell">Measure</div>
                                        <div className="header-cell">Hospital</div>
                                        <div className="header-cell">Value</div>
                                        <div className="header-cell">Actions</div>
                                    </div>

                                    <div className="table-body">
                                        {measureDataEntries.map((entry, index) => (
                                            <div key={entry.id} className="table-row">
                                                <div className="table-cell">
                                                    <select
                                                        value={entry.measure}
                                                        onChange={(e) => updateEntry(entry.id, 'measure', e.target.value)}
                                                        className="measure-select"
                                                        required
                                                    >
                                                        <option value="">Select Measure</option>
                                                        {availableMeasures.map((measure, idx) => (
                                                            <option key={idx} value={measure}>
                                                                {measure}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="table-cell">
                                                    <select
                                                        value={selectedHospital}
                                                        disabled
                                                        className="hospital-select"
                                                        required
                                                    >
                                                        <option value={selectedHospital}>{selectedHospital}</option>
                                                    </select>
                                                </div>

                                                <div className="table-cell">
                                                    <div className="value-input-container">
                                                        <input
                                                            type="number"
                                                            value={entry.value}
                                                            onChange={(e) => updateEntry(entry.id, 'value', e.target.value)}
                                                            placeholder="Enter value"
                                                            className="value-input"
                                                            min="0"
                                                            step="0.01"
                                                            required
                                                        />
                                                        <span className="unit-symbol">
                                                            {entry.measureUnit === "percentage" ? "%" : "#"}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="table-cell">
                                                    <div className="action-buttons-cell">
                                                        <button
                                                            type="button"
                                                            onClick={() => duplicateEntry(entry.id)}
                                                            className="duplicate-btn"
                                                            title="Duplicate this row"
                                                        >
                                                            <Copy01Icon size={16} />
                                                        </button>
                                                        {measureDataEntries.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeEntry(entry.id)}
                                                                className="remove-btn"
                                                                title="Remove this row"
                                                            >
                                                                <Delete01Icon size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <SecondaryButton
                                        onClick={handleCancelClick}
                                        buttonText="Cancel"
                                        disabled={isSaving}
                                    />
                                    <PrimaryButton
                                        buttonText={isSaving ? 'Processing...' : `Save ${measureDataEntries.length} Entries`}
                                        onClick={handleBulkSubmit}
                                        isLoading={isSaving}
                                        processingText={`Processing ${measureDataEntries.length} entries...`}
                                        disabled={isSaving}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                ) : startingDate && endDate && isDateValid && !hasYearData ? (
                    <p className="no-data-message">No measures available for the selected year.</p>
                ) : startingDate && endDate && !isDateValid ? (
                    <p className="date-error">{dateError}</p>
                ) : (
                    <p className="instruction-message">Please select both starting and ending dates to continue.</p>
                )}

                <CancelConfirmation
                    isOpen={showCancelConfirm}
                    onKeepEditing={cancelCancel}
                    onConfirmCancel={confirmCancel}
                />

                {showErrorSummary && errorDetails.length > 0 && (
                    <div className="fixed-error-summary">
                        <h4>Error Summary ({errorDetails.length} failed entries):</h4>
                        <div className="error-list">
                            {errorDetails.map((error, index) => (
                                <div key={index} className="error-item">
                                    {/* <span className="error-row">Failed entries {error.originalIndex}:</span> */}
                                    <span className="error-measure">{error.conflictName || 'Unknown measure'}</span>
                                    <span className="error-message">
                                        {error.status === 409 ? 'Conflict (Data already exists)' : error.message}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <button
                            className="close-error-summary"
                            onClick={() => {
                                setShowErrorSummary(false);
                                toast.dismiss();
                            }}
                        >
                            Close Error Summary
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkAddMeasureData;